import { Fragment, MouseEvent } from 'react';

import {
  TableRow,
  TableRowProps,
  TableCellProps,
  TableCell,
  IconButton,
  Collapse,
  useTheme,
  Theme,
} from '@mui/material';
import { ClassNameMap, makeStyles } from '@mui/styles';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import clsx from 'clsx';

import { DataCell, EditableCell } from '.';
import { TableColumn } from '../../Table.types';
import { AlignTypes } from 'models/commonTypes';
import { getNestedValue } from 'utils/table.utils';

interface EditableTableRowProps extends TableRowProps {
  rowId: number | string;
  columns: TableColumn[];
  classes: ClassNameMap<string>;
  data: Record<string, any>;
  isExpanded: boolean;
  hoverForExpandableRows?: boolean;
  hasExpandableRows?: boolean;
  isSelected?: boolean;
  onExpandRow: (id: any) => void;
  onEditCell: (columnId: string, value: any, row: Record<string, any>) => Promise<any>;
  onRowClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
  onRowDoubleClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
  onRowMouseDown?: (event: MouseEvent<HTMLTableRowElement>) => void;
}

interface RowStyleProps {
  color?: string;
  themeBackground?: string;
  selectedColor?: string;
  hoverColor?: string;
}

const getOverlayBackgroundImage = (...layers: Array<string | undefined>) => {
  const gradients = layers
    .filter(Boolean)
    .map(color => `linear-gradient(${color}, ${color})`);

  return gradients.length ? `${gradients.join(', ')} !important` : 'none';
};

const useStyles = makeStyles<Theme, RowStyleProps>({
  rowBackground: {
    '&>td': {
      backgroundColor: props => (props.color ? `${props.color} !important` : 'inherit'),
    },
  },
  // Opaque base so scrolled columns stay hidden under the sticky cell
  stickyCellWithTheme: {
    backgroundColor: props =>
      props.themeBackground ? `${props.themeBackground} !important` : 'inherit',
  },
  selectedRow: {
    '&>td': {
      backgroundColor: props =>
        props.selectedColor ? `${props.selectedColor} !important` : 'inherit',
    },
  },
  stickySelectedCell: {
    '&&': {
      backgroundColor: props =>
        props.themeBackground ? `${props.themeBackground} !important` : 'inherit',
      backgroundImage: props => getOverlayBackgroundImage(props.selectedColor),
    },
  },
  // Paint every cell the same on hover so sticky matches and the row bg can't outline it
  hoverRow: {
    '&.MuiTableRow-hover:hover': {
      backgroundColor: 'transparent',
      '& > .MuiTableCell-root': {
        backgroundColor: props =>
          props.themeBackground ? `${props.themeBackground} !important` : undefined,
        backgroundImage: props => getOverlayBackgroundImage(props.hoverColor),
      },
    },
  },
  hoverSelectedRow: {
    '&.MuiTableRow-hover:hover': {
      backgroundColor: 'transparent',
      '& > .MuiTableCell-root': {
        backgroundColor: props =>
          props.themeBackground ? `${props.themeBackground} !important` : undefined,
        backgroundImage: props =>
          getOverlayBackgroundImage(props.selectedColor, props.hoverColor),
      },
    },
  },
});
export const EditableRow = ({
  columns,
  data,
  rowId,
  isExpanded,
  classes,
  hoverForExpandableRows,
  hasExpandableRows = false,
  isSelected = false,
  onExpandRow,
  onEditCell,
  onRowClick,
  onRowDoubleClick,
  onRowMouseDown,
  ...rowProps
}: EditableTableRowProps) => {
  const theme = useTheme();

  const renderArrowCell = row =>
    row.expandableContent || row.expandableRow ? (
      <TableCell className={clsx(classes.padding, classes.noBottomBorder)}>
        <IconButton
          size="small"
          onClick={event => {
            event.stopPropagation();
            onExpandRow(rowId);
          }}
        >
          {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </IconButton>
      </TableCell>
    ) : hasExpandableRows ? (
      <TableCell className={classes.padding} />
    ) : null;

  const colorClasses = useStyles({
    color: data.color,
    themeBackground: theme.palette.background.paper,
    selectedColor: theme.palette.action.selected,
    hoverColor: theme.palette.action.hover,
  });

  const emptyCell = () => <TableCell className={classes.padding} />;

  // Wraps the edit cell and send row data on the promise
  const onEditWrapper = (columnId: string, value: any) => onEditCell(columnId, value, data);

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    onRowClick?.(event);
  };

  const handleRowDoubleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    onRowDoubleClick?.(event);
  };

  const handleRowMouseDown = (event: MouseEvent<HTMLTableRowElement>) => {
    onRowMouseDown?.(event);
  };

  const getCellSpan = (cell: unknown) => {
    const isObject = typeof cell === 'object' && cell !== null;
    return {
      colSpan: isObject ? (cell as { colSpan?: number }).colSpan || 1 : 1,
      rowSpan: isObject ? (cell as { rowSpan?: number }).rowSpan || 1 : 1,
    };
  };

  const getDisplayCellValue = (cell: unknown, column: TableColumn) => {
    const isObject = typeof cell === 'object' && cell !== null;
    let cellValue = isObject && !column.formatter ? (cell as { value?: unknown }).value : cell;

    if (typeof cellValue !== 'boolean' && (cellValue === '' || !cellValue)) {
      cellValue = 'N/A';
    }

    return column.formatter ? column.formatter(cellValue, data) : cellValue;
  };

  const renderColumnCell = (column: TableColumn, colIndex: number) => {
    const cell = getNestedValue(data, column.id);
    const { colSpan, rowSpan } = getCellSpan(cell);
    const cellValue = getDisplayCellValue(cell, column);
    const baseCellProps: TableCellProps = {
      align: column.align || AlignTypes.LEFT,
      className: clsx(
        classes.padding,
        (data.expandableContent || data.expandableRow) && classes.noBottomBorder,
        colIndex === 0 && classes.stickyCell,
        colIndex === 0 && !data.color && colorClasses.stickyCellWithTheme,
        colIndex === 0 && isSelected && colorClasses.stickySelectedCell,
      ),
      rowSpan,
      colSpan,
    };

    return column.editable ? (
      <EditableCell
        {...baseCellProps}
        key={`${rowId}-${column.id}`}
        columnId={column.id}
        editControl={column.editControl}
        value={cellValue as string}
        dataType={column.type}
        onCellEdit={onEditWrapper}
        selectableValuesForEnums={column.selectableValuesForEnums}
      />
    ) : (
      <DataCell key={`${rowId}-${column.id}`} data={cellValue as string | number} {...baseCellProps} />
    );
  };

  return (
    <Fragment>
      <TableRow
        {...rowProps}
        className={clsx(
          rowProps.className,
          colorClasses.rowBackground,
          !data.color && colorClasses.hoverRow,
          isSelected && colorClasses.selectedRow,
          isSelected && !data.color && colorClasses.hoverSelectedRow,
        )}
        style={{
          ...rowProps.style,
          cursor: onRowDoubleClick ? 'pointer' : rowProps.style?.cursor,
        }}
        onClick={handleRowClick}
        onDoubleClick={handleRowDoubleClick}
        onMouseDown={handleRowMouseDown}
      >
        {hasExpandableRows && renderArrowCell(data)}
        {data.isEmptyCell && emptyCell()}
        {columns.map(renderColumnCell)}
      </TableRow>
      {data.expandableContent ? (
        <TableRow hover={hoverForExpandableRows}>
          <TableCell className={classes.expandableContentCell} colSpan={columns.length + 1}>
            <Collapse
              in={isExpanded}
              timeout="auto"
              unmountOnExit
              data-testid={`expanded-row-${rowId}`}
            >
              {data.expandableContent}
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
      {isExpanded && data.expandableRow}
    </Fragment>
  );
};
